import React, { useCallback, useEffect, useRef, useState } from 'react';
import axios from 'axios';
import { useLocation, useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { TABLE_SEARCH_EVENT } from './DataTable';
import { formatDate } from '../utils/dateFormat';

const API_URL = process.env.REACT_APP_BACKEND_URL || 'http://localhost:8001';
const MIN_QUERY = 2;

const ICONS = {
  cari: '🏢',
  products: '📦',
  employees: '👤',
  sales_invoices: '🧾',
  purchase_orders: '🛒',
  service_tickets: '🔧',
  sales_quotes: '📄',
  sales_orders: '📋',
  crm_leads: '🎯',
  crm_opportunities: '💼',
  crm_contacts: '📇',
  crm_campaigns: '📣',
};

/**
 * OFF-25: header search. A button (and Ctrl+K / Cmd+K) opens a search box
 * over cari cards, products, employees, sales invoices, purchase orders and
 * service tickets (GET /api/search: only the types the user may read, of
 * their own company). Arrow keys pick a hit, Enter opens it.
 */
export default function GlobalSearch() {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const location = useLocation();
  const [open, setOpen] = useState(false);
  const [text, setText] = useState('');
  const [groups, setGroups] = useState([]);
  const [status, setStatus] = useState('idle'); // idle | loading | ready
  const [active, setActive] = useState(0);
  const inputRef = useRef(null);
  const request = useRef(0);

  const close = useCallback(() => {
    setOpen(false);
    setText('');
    setGroups([]);
    setStatus('idle');
    setActive(0);
  }, []);

  // Ctrl+K / Cmd+K anywhere
  useEffect(() => {
    const onKey = (e) => {
      if ((e.ctrlKey || e.metaKey) && !e.altKey && (e.key === 'k' || e.key === 'K')) {
        e.preventDefault();
        setOpen(true);
      }
    };
    document.addEventListener('keydown', onKey);
    return () => document.removeEventListener('keydown', onKey);
  }, []);

  useEffect(() => {
    if (open) setTimeout(() => inputRef.current?.focus(), 0);
  }, [open]);

  // debounced search
  useEffect(() => {
    const q = text.trim();
    if (q.length < MIN_QUERY) {
      setGroups([]);
      setStatus('idle');
      return undefined;
    }
    setStatus('loading');
    const id = ++request.current;
    const timer = setTimeout(async () => {
      try {
        const res = await axios.get(`${API_URL}/api/search`, { params: { q } });
        if (id !== request.current) return;
        setGroups(res.data?.groups || []);
      } catch (e) {
        if (id !== request.current) return;
        setGroups([]);
      }
      setActive(0);
      setStatus('ready');
    }, 250);
    return () => clearTimeout(timer);
  }, [text]);

  const hits = groups.flatMap((g) => g.items.map((item) => ({ ...item, type: g.type })));

  const go = (hit) => {
    if (!hit?.link) return;
    close();
    const [path, query] = hit.link.split('?');
    navigate(hit.link);
    // the list is already open: its table does not remount, so hand it the search
    if (path === location.pathname && query) {
      const q = new URLSearchParams(query).get('q') || '';
      setTimeout(() => window.dispatchEvent(new CustomEvent(TABLE_SEARCH_EVENT, { detail: q })), 0);
    }
  };

  const onInputKey = (e) => {
    if (e.key === 'Escape') {
      close();
    } else if (e.key === 'ArrowDown') {
      e.preventDefault();
      setActive((a) => Math.min(a + 1, Math.max(hits.length - 1, 0)));
    } else if (e.key === 'ArrowUp') {
      e.preventDefault();
      setActive((a) => Math.max(a - 1, 0));
    } else if (e.key === 'Enter') {
      e.preventDefault();
      go(hits[active]);
    }
  };

  let index = -1;
  return (
    <>
      <button
        type="button"
        onClick={() => setOpen(true)}
        aria-label={t('shell.search.open')}
        title={`${t('shell.search.open')} (Ctrl+K)`}
        data-testid="global-search-button"
        className="flex items-center gap-2 p-2 lg:px-3 lg:py-1.5 rounded-md text-gray-700 dark:text-gray-200 hover:bg-gray-100 dark:hover:bg-gray-700 lg:border lg:border-gray-200 lg:dark:border-gray-600 transition"
      >
        <svg className="w-5 h-5 lg:w-4 lg:h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24" aria-hidden="true">
          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M21 21l-4.35-4.35M11 18a7 7 0 100-14 7 7 0 000 14z" />
        </svg>
        <span className="hidden lg:inline text-sm text-gray-500 dark:text-gray-400">{t('shell.search.placeholderShort')}</span>
        <kbd className="hidden lg:inline text-[10px] text-gray-400 border border-gray-200 dark:border-gray-600 rounded px-1">Ctrl K</kbd>
      </button>

      {open && (
        <div className="fixed inset-0 z-[60] bg-black/40 flex items-start justify-center px-2 pt-16 sm:pt-24" onMouseDown={close}>
          <div
            role="dialog"
            aria-modal="true"
            aria-label={t('shell.search.open')}
            className="w-full max-w-xl bg-white dark:bg-gray-800 rounded-lg shadow-xl overflow-hidden"
            onMouseDown={(e) => e.stopPropagation()}
          >
            <div className="flex items-center gap-2 px-3 border-b border-gray-200 dark:border-gray-700">
              <svg className="w-5 h-5 text-gray-400" fill="none" stroke="currentColor" viewBox="0 0 24 24" aria-hidden="true">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M21 21l-4.35-4.35M11 18a7 7 0 100-14 7 7 0 000 14z" />
              </svg>
              <input
                ref={inputRef}
                type="search"
                value={text}
                onChange={(e) => setText(e.target.value)}
                onKeyDown={onInputKey}
                placeholder={t('shell.search.placeholder')}
                aria-label={t('shell.search.open')}
                data-testid="global-search-input"
                className="flex-1 py-3 bg-transparent text-sm text-gray-900 dark:text-gray-100 outline-none border-0 focus:ring-0"
              />
              <button type="button" onClick={close} className="text-xs text-gray-400 hover:text-gray-600 px-1" aria-label={t('common.close')}>
                Esc
              </button>
            </div>
            <div className="max-h-[60vh] overflow-y-auto" data-testid="global-search-results">
              {status === 'idle' && (
                <p className="px-4 py-6 text-sm text-center text-gray-500 dark:text-gray-400">{t('shell.search.hint')}</p>
              )}
              {status === 'loading' && !hits.length && (
                <p className="px-4 py-6 text-sm text-center text-gray-500 dark:text-gray-400">{t('common.loading')}</p>
              )}
              {status === 'ready' && !hits.length && (
                <p className="px-4 py-6 text-sm text-center text-gray-500 dark:text-gray-400">{t('shell.search.noResults')}</p>
              )}
              {groups.map((g) => (
                <div key={g.type} className="py-1">
                  <div className="px-4 pt-2 pb-1 text-[11px] font-semibold uppercase tracking-wide text-gray-500 dark:text-gray-400">
                    {t(`shell.search.types.${g.type}`)}
                  </div>
                  {g.items.map((item) => {
                    index += 1;
                    const mine = index;
                    return (
                      <button
                        key={`${g.type}-${item.id}`}
                        type="button"
                        onClick={() => go(item)}
                        onMouseEnter={() => setActive(mine)}
                        className={`w-full flex items-center gap-3 px-4 py-2 text-start ${mine === active ? 'bg-primary-50 dark:bg-gray-700' : ''}`}
                      >
                        <span aria-hidden="true">{ICONS[g.type] || '•'}</span>
                        <span className="min-w-0">
                          <span className="block text-sm text-gray-900 dark:text-gray-100 truncate">{item.title}</span>
                          {(item.subtitle || item.date) && (
                            <span className="block text-xs text-gray-500 dark:text-gray-400 truncate">
                              {[item.subtitle, item.date && formatDate(item.date)].filter(Boolean).join(' · ')}
                            </span>
                          )}
                        </span>
                      </button>
                    );
                  })}
                </div>
              ))}
            </div>
          </div>
        </div>
      )}
    </>
  );
}
